/**
 * Module: web/src/stores/assistedQueue.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.1.0
 * Date: 2026-07-16
 *
 * v1.1.0 (reviewer context pack -- contract assisted-entry-api v1.2.0 SS1):
 * load() gains the server-side sort/filter params (sort newest/oldest/
 * est_lost, from/to CT civil dates, repeatable reason codes) the queue
 * view's new control row drives. pending_count stays whatever the server
 * says -- it is UNFILTERED global truth by contract.
 *
 * Assisted-Entry Review Queue store (P6): the queue list + the shared
 * pending-count the AppShell nav badge renders. One store so every surface
 * that actions a draft (queue view, detail view, /dispatch?draft= wizard
 * submit) can refresh the same count without prop-drilling.
 *
 * Count freshness is action-driven, not polled: the drafter runs a few times
 * a day (decision #127 cadence honesty -- the queue header says so), so the
 * badge refreshes on shell mount, on queue loads, and after every
 * approve/edit/dismiss.
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { apiGet } from '@/composables/useApi';
import type {
  AssistedDraftListResponse,
  AssistedDraftSort,
  AssistedDraftSummary,
  DraftReviewState,
} from '@/types/api';

/** Server-side list filters (contract SS1 v1.2.0; all optional/additive). */
export interface AssistedQueueFilters {
  /** Sort vocabulary; omitted = 'priority' (the server default). */
  sort?: AssistedDraftSort;
  /** CT civil date YYYY-MM-DD, inclusive; omitted = unbounded. */
  from?: string;
  to?: string;
  /** reasons.code values -- primary OR m2m intersect. */
  reasons?: string[];
}

export const useAssistedQueueStore = defineStore('assistedQueue', () => {
  const drafts = ref<AssistedDraftSummary[]>([]);
  const pendingCount = ref<number | null>(null); // null = never loaded
  const loading = ref(false);
  const errorMessage = ref('');

  /** Load the queue for one state filter ('all' = every state). */
  async function load(
    state: DraftReviewState | 'all' = 'pending',
    filters: AssistedQueueFilters = {},
  ): Promise<void> {
    loading.value = true;
    errorMessage.value = '';
    try {
      const qs = new URLSearchParams({ state, sort: filters.sort ?? 'priority' });
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
      for (const code of filters.reasons ?? []) qs.append('reason', code);
      const resp = await apiGet<AssistedDraftListResponse>('/assisted-entry/drafts', qs.toString());
      drafts.value = resp.drafts;
      pendingCount.value = resp.pending_count;
    } catch {
      errorMessage.value = 'Could not load the review queue. Try again.';
    } finally {
      loading.value = false;
    }
  }

  /** Badge-only refresh (AppShell mount; after wizard draft-mode submits). */
  async function refreshCount(): Promise<void> {
    try {
      const resp = await apiGet<AssistedDraftListResponse>(
        '/assisted-entry/drafts',
        new URLSearchParams({ state: 'pending' }).toString(),
      );
      pendingCount.value = resp.pending_count;
    } catch {
      /* badge is cosmetic -- a failed refresh keeps the last known value */
    }
  }

  return { drafts, pendingCount, loading, errorMessage, load, refreshCount };
});
